import type { SearchResultClickEvent } from '../result-page/search-tab/SearchResultClickEvent';
import type { ViewChangeEvent } from '../result-page/search-tab/ViewChangeEvent';
import type { SearchResultsPresentationEvent } from '../result-page/search-tab/SearchResultsPresentationEvent';
import type { TabAnalyticsEvent } from '../result-page/tabs-header/TabAnalyticsEvent';
import type { MoreTabsAnalyticsEvent } from '../result-page/tabs-header/MoreTabsAnalyticsEvent';
import type { PaginationEvent } from '../result-page/search-tab/PaginationEvent';
import type {UiaasSearchAnalyticsReportingSeComponent} from './se/uiaas-search-analytics-reporting-se.component';
import type {UiaasSearchAnalyticsReportingGuidedComponent} from './guided/uiaas-analytics-reporting-guided.component';
import type {UiaasSearchAnalyticsReportingSvsComponent} from './svs/uiaas-search-analytics-reporting-svs.component';
import type {AnalyticsGenericEvent} from '../../utils/analytics/AnalyticsGenericEvent';

type TrackingComponent =
    | UiaasSearchAnalyticsReportingSeComponent
    | UiaasSearchAnalyticsReportingSvsComponent
    | UiaasSearchAnalyticsReportingGuidedComponent;

export type AnalyticsEvent =
    | SearchResultClickEvent
    | ViewChangeEvent
    | SearchResultsPresentationEvent
    | TabAnalyticsEvent
    | MoreTabsAnalyticsEvent
    | PaginationEvent
    | AnalyticsGenericEvent;

export function getEnv(component?:TrackingComponent): String {
    //if passed explicitly, use it
    if (component && component.env) {
      return component.env;
    }
    //otherwise, rely on heuristic to guess which env are we
    const hostname = window.location.hostname;

    const production = [
      'www.se.com',
      'www.schneider-electric.cn',
      'www.apc.com',
    ];

    return production.indexOf(hostname) > -1 ? 'Production' : 'UAT';
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function getDigitalPlatform(component: TrackingComponent, ignored: CustomEvent): String {
    if (component && component.digitalPlatform) {
      return component.digitalPlatform;
    }

    const hostname = (window.location.hostname =
        window.location.hostname.replace('www.', ''));

    if (hostname == 'se.com') {
      return 'se.com';
    }

    if (hostname == 'schneider-electric.cn') {
      return 'se.com';
    }

    if (hostname == 'apc.com') {
      return 'apc.com';
    }

    const href = window.location.href;
    if (href.toLowerCase().indexOf('.se.com/myschneider/') !== -1) {
      return 'seamless';
    }

    if (href.toLowerCase().indexOf('amplifyapp.com/myschneider') !== -1) {
      return 'seamlessUAT';
    }

    // this is our playground for uiaas, it should be accounted as DEV env for Seamless
    if (href.startsWith('https://s3-assets-dev.search.se.com/uiaas-search/')) {
      return 'SearchUiaasDEV';
    }

    return 'se.com';
}

export function pushToDataLayer(details: Object): void {
    const dataLayer = window.dataLayer !== undefined ? window['dataLayer'] : [];
    if(!details?.['siteSearchKeyword']) {
      delete details?.['siteSearchKeyword'];
    }
    if(!details?.['destinationURL']) {
      delete details?.['destinationURL'];
    }

    const reportedEvent = {
      ...details,
    };

    if (dataLayer) {
      dataLayer.push(reportedEvent);
    }
}

function htmlentities(str:string): string {
    return str
      .toString()
      .replace('&', '&amp;')
      .replace('\'', '&#39;')
      .replace('"', '&quot;')
      .replace('<', '&lt;')
      .replace('>', '&gt;');
}

export function object2attributes(obj: Object = {}): string {
    return Object
      .entries(obj)
      .map(([key, value])=>[
        htmlentities(key),
        htmlentities(value)
      ])
      .map(([key, value]) => `${key}="${value}"`)
      .join(' ');
}

export function camelize(text) {
    const a = text.toLowerCase()
      .replace(/[-_\s.]+(.)?/g, (_, c) => c ? c.toUpperCase() : '');
    return a.substring(0, 1).toLowerCase() + a.substring(1);
}

export function getSiteSearchCategory(
  tabName: string,
  appName: string
) {
    if (tabName) {
      if (tabName === 'restricted/faq') {
        return appName === 'faq' ? 'restricted/faq' : appName === 'support' ? 'helpCenter/faq' : 'se/faq';
      }

      if (['se/new_all', 'se/all', 'se/new_all_faq'].includes(tabName)) {
        return appName === 'support' ? tabName.replace('se', 'helpCenter') : tabName;
      }

      if (tabName.includes('se/support')) {
        return tabName.replace('se/support', 'helpCenter');
      }

      if (tabName.includes('myschneider/support')) {
        return tabName.replace('myschneider/support', 'helpCenter');
      }
    }

    return tabName;
}

export function getAnalyticsCat(appName: string) {
    switch (appName) {
    case 'support':
      return 'Help Center';
    case 'faq':
      return 'Federated Search';
    default:
      return 'Global Search';
    }
}

/**
Check if event target comes from FAQ Guided search (so we have double guided search widget, one regular and one for FAQ) and returns 'FAQ' if it does.
*/
export function getGuidedSearchCategory(
  target: EventTarget
) {
    switch (target['id']) {
    case 'faq-guided':
      return 'FAQ ';
    case 'support-guided':
      return 'Help Center ';  
    default:
      return '';
    }
}
